import { useAppDispatch } from '@hooks/useAppDispatch';
import { getCartStatus } from '@api/interfaces/cartApi';
import { setConflictingModalStoreId } from '@slices/conflictingModalSlice';
import showCartConflictingModal from '@helpers/showCartConflictingModal';
import { getOrderStatus } from '@api/interfaces/orderApi';
import { getListStatus, getWishListEntriesStatus } from '@api/interfaces/wishlistApi';
import { initCheckout } from '@api/interfaces/checkoutApi';
import { WishListFormEntry } from '@helpers/wishlistEntriesToForm';
import type { AxfoodCartEntryStatusViewModel } from '@occ/api-client';
import useCustomer from '@hooks/useCustomer';
import useAbortController from '@hooks/useAbortController';

/**
 * Iterate over the returned items to see which products are conflicting in the cart
 */
const getCartConflictingItems = (isB2B: boolean, data?: Array<AxfoodCartEntryStatusViewModel>) => {
  return data?.filter((item) => {
    return (
      item.priceAbscent ||
      item.outOfStock ||
      item.partialOutOfStock ||
      !item.salableOnline ||
      item.notAllowedAnonymous ||
      item.notAllowedExternalPickupLocation ||
      item.notAllowedBulkyProduct ||
      // @ts-ignore
      (isB2B ? item.notAllowedB2b || item.entryProduct.notAllowedB2b : false)
    );
  });
};

export const enterCheckoutReason = 'Enter checkout';
export const addOrderToCartReason = 'Add order to cart';
export const addListToCartReason = 'Add List to cart';

interface Params {
  storeId?: string;
  slotCode?: string;
  orderID?: string;
  listID?: string;
  wishlistEntries?: Array<WishListFormEntry>;
}

interface Props {
  initializeCheckout?: boolean;
  checkStock?: boolean;
}

/**
 * Returns an async function that triggers the conflict modal if the cart status returns items that are not sellable
 */
const useCheckCartConflict = ({ initializeCheckout, checkStock }: Props = {}) => {
  const dispatch = useAppDispatch();
  const { getSignal } = useAbortController();
  const { customer } = useCustomer();

  // Fetches the status of the items that are about to end up in the cart, and the reason to show if they conflict
  const getStatus = ({
    storeId,
    orderID,
    listID,
    wishlistEntries,
    slotCode,
  }: Params): Promise<{ items?: Array<AxfoodCartEntryStatusViewModel>; reason?: string }> => {
    if (initializeCheckout) {
      return initCheckout().then((status) => ({ items: status.cartStatus, reason: enterCheckoutReason }));
    }
    if (orderID) {
      return getOrderStatus(orderID).then((status) => ({ items: status, reason: addOrderToCartReason }));
    }
    if (listID) {
      return getListStatus(listID).then((status) => ({ items: status.data, reason: addListToCartReason }));
    }
    if (wishlistEntries) {
      return getWishListEntriesStatus({ entries: wishlistEntries }, getSignal()).then((status) => ({
        items: status.data,
        reason: addListToCartReason,
      }));
    }
    return getCartStatus(slotCode, storeId, checkStock, getSignal()).then((status) => ({
      items: status.data.cartStatus,
      reason: status.data.reason,
    }));
  };

  return (params: Params) =>
    new Promise((resolve, reject) => {
      getStatus(params)
        .then(({ items, reason }) => {
          const conflictingItems = getCartConflictingItems(customer!?.isB2BCustomer, items);
          if (params.storeId) dispatch(setConflictingModalStoreId(params.storeId));
          showCartConflictingModal(conflictingItems ?? [], resolve, reject, dispatch, reason);
        })
        // A failed request (aborted, 401, network error) must reject, otherwise the caller waits forever
        .catch(reject);
    });
};

export default useCheckCartConflict;
